import { type ComponentProps } from 'react'
import styled from 'styled-components'
import { badgeStyle } from '@/features/shared/elements/Badges/config'
import { type BadgeProps } from '@/features/shared/elements/Badges/types'
import { type StaticColor } from '@/features/shared/theme/staticColors'
import { Body } from '@shipt/design-system-typography'

type Colors = { color: StaticColor; bgColor: StaticColor }
type BgColor = Pick<Colors, 'bgColor'>
type Variant = { variant: BadgeVariant }
type ColorsWithChildren = Colors & { children: React.ReactNode }

const StyledRectangleVariant = styled(Body)<Colors>`
  display: block;
  width: fit-content;
  line-height: 0.9em;
  color: ${({ theme, color }) => theme[color]};
  background-color: ${({ theme, bgColor }) => theme[bgColor]};
  border-radius: 4px;
  padding: 5px 8px;
  white-space: nowrap;
`

const RectangleVariant = (props: ComponentProps<typeof StyledRectangleVariant>) => (
  <StyledRectangleVariant {...props} as="span" strong size="md" />
)

const FlagContainer = styled(RectangleVariant)`
  position: relative;
  border: 2px solid white;
  border-right: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  padding-right: 3px;
`

const FlagTriangle = styled.div<BgColor & { 'data-position': 'top' | 'bottom' }>`
  position: absolute;
  left: 100%;
  border-left: 13px solid white;

  &[data-position='top'] {
    top: -2px;
    border-top: 0 solid transparent;
    border-bottom: 18px solid transparent;
  }

  &[data-position='bottom'] {
    bottom: -2px;
    border-bottom: 0 solid transparent;
    border-top: 18px solid transparent;
  }

  &::after {
    content: '';
    position: absolute;
    right: 4px;
    border-left: 10px solid ${({ theme, bgColor }) => theme[bgColor]};
    z-index: 1;
  }

  &[data-position='top']::after {
    top: 2px;
    border-top: 0 solid transparent;
    border-bottom: 14px solid transparent;
  }

  &[data-position='bottom']::after {
    bottom: 2px;
    border-bottom: 0 solid transparent;
    border-top: 14px solid transparent;
  }
`

const FlagVariant = ({
  children,
  className,
  color,
  bgColor,
}: ColorsWithChildren & { className?: string }) => {
  return (
    <div className={className}>
      <FlagContainer color={color} bgColor={bgColor}>
        {children}
        <FlagTriangle bgColor={bgColor} data-position="top" />
        <FlagTriangle bgColor={bgColor} data-position="bottom" />
      </FlagContainer>
    </div>
  )
}

export enum BadgeVariant {
  rectangle = 'rectangle',
  flag = 'flag',
}

const BadgeVariantComponents = {
  [BadgeVariant.rectangle]: RectangleVariant,
  [BadgeVariant.flag]: FlagVariant,
}

type BaseBadgeProps = BadgeProps & Variant
const Badge = ({ className, limit, type, variant = BadgeVariant.rectangle }: BaseBadgeProps) => {
  const Component = BadgeVariantComponents[variant]
  return (
    <Component
      color={badgeStyle[type].color}
      bgColor={badgeStyle[type].bgColor}
      className={className}
      aria-label={badgeStyle[type].badgeCopy}
    >
      {badgeStyle[type].badgeCopy}
      {(type === 'limit' || type === 'limitV2') && limit}
    </Component>
  )
}

export const RectangleBadge = (props: BadgeProps) => (
  <Badge variant={BadgeVariant.rectangle} {...props} />
)

export const FlagBadge = (props: BadgeProps) => <Badge variant={BadgeVariant.flag} {...props} />

type CustomBadgeProps = ColorsWithChildren & Variant & { className?: string }

export const CustomBadge = ({
  children,
  variant = BadgeVariant.rectangle,
  ...props
}: CustomBadgeProps) => {
  const Component = BadgeVariantComponents[variant]
  return <Component {...props}>{children}</Component>
}

export const SquareBadge = styled(Body)`
  width: fit-content;
  padding: 2px 4px;
  border-radius: 3px;
  color: ${({ theme }) => theme.purple500};
  background-color: ${({ theme }) => theme.purple100};
`
